import { create } from "zustand";
import api, { apiFormData, isAbortError } from "../../utils/api";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";

export interface Aviso {
  id: string;
  title: string | null;
  description: string | null;
  image: string | null;
  status: "active" | "inactive";
  company_id?: string | null;
  readed_by?: string[];
  createdBy?: string | null;
  createdAt?: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
}

export interface AvisoFormPayload {
  title?: string;
  description?: string;
  image?: File | null;
  removeImage?: boolean;
  status?: "active" | "inactive";
  /** Empty string or "all" → global aviso (visible to every company). */
  company_id?: string | null;
}

interface AvisosStore {
  avisos: Aviso[];
  activeAvisos: Aviso[];
  loading: boolean;
  loadingActive: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };

  fetchAvisos: (params?: {
    limit?: number;
    page?: number;
    status?: string;
    search?: string;
    scope?: "global" | "company";
  }) => Promise<void>;
  fetchActiveAvisos: () => Promise<Aviso[]>;
  createAviso: (
    payload: AvisoFormPayload,
  ) => Promise<{ success: boolean; data?: Aviso; error?: string }>;
  updateAviso: (
    id: string,
    payload: AvisoFormPayload,
  ) => Promise<{ success: boolean; data?: Aviso; error?: string }>;
  deleteAviso: (id: string) => Promise<{ success: boolean; error?: string }>;
  markAvisoAsRead: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

const buildFormData = (payload: AvisoFormPayload): FormData => {
  const fd = new FormData();
  if (payload.title !== undefined) fd.append("title", payload.title ?? "");
  if (payload.description !== undefined)
    fd.append("description", payload.description ?? "");
  if (payload.status !== undefined) fd.append("status", payload.status);
  // Always send company_id when caller specified the key (even when empty/null,
  // so the backend can switch the aviso between company-scoped and global).
  if (Object.prototype.hasOwnProperty.call(payload, "company_id")) {
    fd.append("company_id", payload.company_id == null ? "" : payload.company_id);
  }
  if (payload.image instanceof File) fd.append("image", payload.image);
  if (payload.removeImage) fd.append("removeImage", "true");
  return fd;
};

// Shared in-flight promise so concurrent callers (e.g. React StrictMode
// double-effects, or several components mounting at once) hit the network
// once and all receive the same fresh data. Without this, the second caller
// short-circuited on `loadingActive` and got the stale (often empty)
// `activeAvisos`, which caused the login modal to flash and then disappear
// when navigating before clicking "Entendido".
let activeAvisosFetchPromise: Promise<Aviso[]> | null = null;

export const useAvisosStore = create<AvisosStore>((set, get) => ({
  avisos: [],
  activeAvisos: [],
  loading: false,
  loadingActive: false,
  error: null,
  meta: undefined,

  fetchAvisos: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, error: null });

    try {
      const { data } = await api.get("/avisosList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          status: params.status || undefined,
          search: params.search || undefined,
          scope: params.scope || undefined,
        },
      });

      if (getAdminFetchGeneration() !== fetchGen) {
        set({ loading: false });
        return;
      }

      set({
        avisos: data.data || [],
        meta: data.meta,
        loading: false,
      });
    } catch (err: unknown) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) {
        set({ loading: false });
        return;
      }
      set({ error: getError(err), loading: false });
    }
  },

  fetchActiveAvisos: async () => {
    if (activeAvisosFetchPromise) return activeAvisosFetchPromise;

    set({ loadingActive: true, error: null });
    activeAvisosFetchPromise = (async () => {
      try {
        const { data } = await api.get("/avisosActive");
        const list: Aviso[] = data?.data?.data || data?.data || [];
        set({ activeAvisos: list, loadingActive: false });
        return list;
      } catch (err: unknown) {
        set({ loadingActive: false, error: getError(err) });
        return [];
      } finally {
        activeAvisosFetchPromise = null;
      }
    })();

    return activeAvisosFetchPromise;
  },

  createAviso: async (payload) => {
    try {
      const fd = buildFormData(payload);
      const { data } = await apiFormData.post("/aviso", fd);
      const created: Aviso = data?.data?.data || data?.data;

      set((state) => ({
        avisos: created ? [created, ...state.avisos] : state.avisos,
      }));

      return { success: true, data: created };
    } catch (err: unknown) {
      return { success: false, error: getError(err) };
    }
  },

  updateAviso: async (id, payload) => {
    try {
      const fd = buildFormData(payload);
      const { data } = await apiFormData.put("/aviso", fd, {
        params: { id },
      });
      const updated: Aviso = data?.data?.data || data?.data;

      set((state) => ({
        avisos: state.avisos.map((a) =>
          a.id === id ? { ...a, ...updated } : a,
        ),
      }));

      return { success: true, data: updated };
    } catch (err: unknown) {
      return { success: false, error: getError(err) };
    }
  },

  deleteAviso: async (id) => {
    try {
      await api.delete("/aviso", { params: { id } });
      set((state) => ({
        avisos: state.avisos.filter((a) => a.id !== id),
      }));
      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: getError(err) };
    }
  },

  markAvisoAsRead: async (id) => {
    try {
      await api.post("/aviso/read", { id });
      set((state) => ({
        activeAvisos: state.activeAvisos.filter((a) => a.id !== id),
      }));
      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: getError(err) };
    }
  },
}));
